import { useEffect, useState } from 'react';

import { DropDownListComponent } from '@syncfusion/ej2-react-dropdowns';
import { AxiosError } from 'axios';
import moment from 'moment';
import { Col, Row } from 'react-bootstrap';
import { useForm } from 'react-hook-form';

import { requests } from 'common/axios';
import ControlledProviderDataTextBox from 'components/Common/CustomTextBoxWrapper/ControlledProviderDataTextBox';
import { ControlledSyncfusionDatePicker } from 'components/Common/SyncfusionWrappers/Syncfusion';
import { IPADetail, IIpaNetworkMapping, IIpaNetworkMappingSubmitResponse } from 'components/ProviderData/IpaAdministration/atoms/Models';
import {
    IpaNetworkMappingService,
} from 'components/ProviderData/subjects/IpaNetworkMappingService';
import DisabledWrapper from 'components/ProviderData/utils/DisabledWrapper';
import { dialogService } from 'subjects/common/DialogService';

type Props = {
    network: IIpaNetworkMapping | null;
    ipa: IPADetail;
    onFormDirtyChange: (isDirty: boolean) => void;
    isReadOnlyViewer: boolean;
}

export const IpaNetworkMappingForm = ({ network, ipa, onFormDirtyChange, isReadOnlyViewer }: Props) => {
    const {
        control,
        register,
        handleSubmit,
        reset,
        setValue,
        getValues,
        formState: { errors, isDirty },
        watch,
    } = useForm<any>();

    const [startDate, setStartDate] = useState<Date>();
    const [networkMapDetail, setNetworkMapDetail] = useState<IIpaNetworkMapping>();
    const [contractCodeList, setContractCodeList] = useState();

    const getNetworkMappingDetail = (id: string) => {
        const searchNetworkMappingUrl = `/api/IpaWebNetworkMapping/GetNetworkMappingDetail?id=${id}`;
        requests.get<any>(searchNetworkMappingUrl).then((data) => {
            setNetworkMapDetail(data);
        });
    };

    const getContractCodeList = () => {
        const getContractCodeUrl = `/api/IpaPlan/GetContractCodes?companyCode=N/A`;
        requests.get<any>(getContractCodeUrl).then((data) => {
            setContractCodeList(data);
        });
    };

    const saveInformation = async (data: IIpaNetworkMapping) => {
        try {
            dialogService.loading(true);
            const adaptedData = {
                ...data,
                ipaCode: ipa.ipaCode,
                ipaId: ipa.id,
            };

            let submitResponse: IIpaNetworkMappingSubmitResponse | null = null;

            if (network?.id) {
                adaptedData.id = network?.id;
                submitResponse = (await requests.put('/api/IpaWebNetworkMapping/UpdateNetworkMapping', {
                    data: { ...network, ...networkMapDetail, ...adaptedData },
                })) as IIpaNetworkMappingSubmitResponse;
            } else {
                submitResponse = (await requests.post('/api/IpaWebNetworkMapping/CreateNetworkMapping', {
                    data: adaptedData,
                })) as IIpaNetworkMappingSubmitResponse;
            }
            IpaNetworkMappingService.submit({ requestFailed: false, data: submitResponse });
        } catch (e) {
            const message = `${(e as AxiosError)?.response?.data}`;
            IpaNetworkMappingService.submit({ requestFailed: true, requestErrorMessage: message });
        } finally {
            dialogService.loading(false);
        }
    };

    const PcpData = [
        { label: 'Yes', value: 'yes' },
        { label: 'No', value: 'no' },
    ];

    useEffect(() => {
        getContractCodeList();
        if (network?.id) {
            getNetworkMappingDetail(network.id);
        } else {
            reset();
        }
    }, []);

    useEffect(() => {
        const subscription = IpaNetworkMappingService.submit$().subscribe((response) => {
            if (response === undefined) {
                handleSubmit(saveInformation)();
            }

            if (response && !response.requestFailed) {
                dialogService.close();
            }
        });

        if (network?.id) {
            for (const c in getValues()) {
                setValue(c, network[c]);
            }
        } else {
            reset();
        }

        return () => {
            subscription.unsubscribe();
        };
    }, [network]);

    useEffect(() => {
        onFormDirtyChange(isDirty);
    }, [watch]);

    return (
        <DisabledWrapper disabled={isReadOnlyViewer}>
            <form>
                <Row className='mb-3'>
                    {network?.networkMapId && (
                        <Col md='6'>
                            <div>
                                <div className={`e-float-input`}>
                                    <label>Network Mapping ID</label>
                                    <br />
                                    <label>{network?.networkMapId}</label>
                                </div>
                            </div>
                        </Col>
                    )}
                    <Col md='6'>
                        <div>
                            <div className={`e-float-input`}>
                                <label>IPA Code</label>
                                <br />
                                <label>{ipa.ipaCode}</label>
                            </div>
                        </div>
                    </Col>
                </Row>

                <Row className='mb-3'>
                    <Col md='6'>
                        <div className='no-padding'>
                            <label className='e-label-select mb-1'>Agreement Start Date *</label>
                            <ControlledSyncfusionDatePicker
                                format='MM/dd/yyyy'
                                name='effectiveStartDate'
                                control={control}
                                dpAttrs={{
                                    showClearButton: true,
                                    placeholder: 'Start date',
                                    min: undefined,
                                    max: new Date(9999, 11, 31),
                                    strictMode: true,
                                    enableMask: true,
                                }}
                                defaultValue={moment(
                                    new Date(new Date().getFullYear(), new Date().getMonth(), 1),
                                ).format('MM/DD/YYYY')}
                                isRetrievingDateAsString={true}
                                rules={{
                                    required: 'Start date is required',
                                }}
                                onChangeHandler={({ valueAsDate }: { valueAsDate: Date }) => {
                                    setStartDate(valueAsDate);
                                }}
                            />
                            {errors.effectiveStartDate && (
                                <p className='error-form'>{errors.effectiveStartDate.message}</p>
                            )}
                        </div>
                    </Col>
                    <Col md='6'>
                        <div className='no-padding'>
                            <label className='e-label-select mb-1'>Agreement End Date</label>
                            <ControlledSyncfusionDatePicker
                                name='effectiveEndDate'
                                format='MM/dd/yyyy'
                                control={control}
                                defaultValue={getValues('effectiveEndDate') ?? '12/31/9999'}
                                dpAttrs={{
                                    showClearButton: true,
                                    placeholder: 'End date',
                                    min: startDate,
                                    max: new Date(9999, 11, 31),
                                    strictMode: true,
                                    enableMask: true,
                                }}
                                isRetrievingDateAsString={true}
                                rules={{
                                    validate: {
                                        isBeforeCaseDueDate: (value) => {
                                            let effStartDate = getValues('effectiveStartDate');
                                            effStartDate = effStartDate ? new Date(effStartDate) : new Date();
                                            const endDate = value ? new Date(value) : null;
                                            if (endDate && endDate < effStartDate) {
                                                return 'End date must be on or after start date';
                                            }
                                            return true;
                                        },
                                    },
                                }}
                            />
                            {errors.effectiveEndDate && <p className='error-form'>{errors.effectiveEndDate.message}</p>}
                        </div>
                    </Col>
                </Row>

                <Row className='mb-3'>
                    <Col
                        className='field-col'
                        md={6}
                    >
                        <div className='e-input-group-margin'>
                            <label className='e-label-select mb-1'>Contract Code *</label>
                            <DropDownListComponent
                                id='contractCode'
                                {...register('contractCode', {
                                    required: 'Contract Code is required',
                                })}
                                value={contractCodeList && getValues('contractCode')}
                                dataSource={contractCodeList}
                                fields={{ text: 'text', value: 'id' }}
                                placeholder='Contract Code'
                                change={({ value }) => {
                                    setValue('contractCode', value);
                                }}
                                popupHeight='220px'
                            />
                            {errors.contractCode && <p className='error-form'>Contract Code is Required</p>}
                        </div>
                    </Col>
                    <Col
                        className='field-col'
                        md={6}
                    >
                        <div className='e-input-group-margin'>
                            <label className='e-label-select mb-1'>PCP Flag *</label>
                            <DropDownListComponent
                                id='isPcp'
                                {...register('isPcp', {
                                    required: 'PCP Flag is required',
                                })}
                                dataSource={PcpData}
                                fields={{ text: 'label', value: 'value' }}
                                placeholder='Select Yes/No'
                                change={({ value }) => {
                                    setValue('isPcp', value);
                                }}
                                popupHeight='220px'
                            />
                            {errors?.isPcp && <p className='error-form'>PCP Flag is required</p>}
                        </div>
                    </Col>
                </Row>

                <Row className='mb-3'>
                    <Col md='4'>
                        <div>
                            <label className='col-md-12 mb-1'>TIN</label>
                            <ControlledProviderDataTextBox
                                name='tin'
                                control={control}
                                rules={{
                                    validate: {
                                        isNotEmpty: (value) => {
                                            if (isNaN(value)) return 'TIN must be a number';
                                            return true;
                                        },
                                    },
                                }}
                            />
                        </div>
                        {errors?.tin && <p className='error-form'>TIN must be a number</p>}
                    </Col>
                    <Col md='4'>
                        <div>
                            <label className='col-md-12 mb-1'>Provider Class</label>
                            <ControlledProviderDataTextBox
                                name='class'
                                control={control}
                                boxAttrs={{
                                    htmlAttributes: {
                                        maxlength: '100',
                                    },
                                }}
                            />
                        </div>
                    </Col>
                    <Col md='4'>
                        <div>
                            <label className='col-md-12 mb-1'>Provider Specialty</label>
                            <ControlledProviderDataTextBox
                                name='specialty'
                                control={control}
                                boxAttrs={{
                                    htmlAttributes: {
                                        maxlength: '255',
                                    },
                                }}
                            />
                        </div>
                    </Col>
                </Row>

                <Row className='mb-3'>
                    <Col md='4'>
                        <div>
                            <label className='col-md-12 mb-1'>Network ID *</label>
                            <ControlledProviderDataTextBox
                                name='networkId'
                                control={control}
                                boxAttrs={{
                                    htmlAttributes: {
                                        maxlength: '100',
                                    },
                                }}
                                rules={{ required: 'Network ID is Required' }}
                            />
                        </div>
                        {errors.networkId && <p className='error-form'>{errors.networkId.message}</p>}
                    </Col>
                    <Col md='4'>
                        <div>
                            <label className='col-md-12 mb-1'>Network Prefix *</label>
                            <ControlledProviderDataTextBox
                                name='networkPrefix'
                                control={control}
                                boxAttrs={{
                                    htmlAttributes: {
                                        maxlength: '100',
                                    },
                                }}
                                rules={{ required: 'Network Prefix is Required' }}
                            />
                            {errors.networkPrefix && <p className='error-form'>{errors.networkPrefix.message}</p>}
                        </div>
                    </Col>
                    <Col md='4'>
                        <div>
                            <label className='col-md-12 mb-1'>Pricing Agreement ID *</label>
                            <ControlledProviderDataTextBox
                                name='agreementId'
                                control={control}
                                boxAttrs={{
                                    htmlAttributes: {
                                        maxlength: '100',
                                    },
                                }}
                                rules={{ required: 'Agreement ID is Required' }}
                            />
                            {errors.agreementId && <p className='error-form'>{errors.agreementId.message}</p>}
                        </div>
                    </Col>
                </Row>

                <Row className='mb-2'>
                    <label className='fw-bold'>IPA:</label>
                </Row>
                <Row>
                    <Col md='4'>
                        <div>
                            <label className='col-md-12 mb-1'>Network ID *</label>
                            <ControlledProviderDataTextBox
                                name='ipaNetworkId'
                                control={control}
                                boxAttrs={{
                                    htmlAttributes: {
                                        maxlength: '100',
                                    },
                                }}
                                rules={{ required: 'Network ID is Required' }}
                            />
                            {errors.ipaNetworkId && <p className='error-form'>{errors.ipaNetworkId.message}</p>}
                        </div>
                    </Col>
                    <Col md='4'>
                        <div>
                            <label className='col-md-12 mb-1'>Network Prefix *</label>
                            <ControlledProviderDataTextBox
                                name='ipaNetworkPrefix'
                                control={control}
                                boxAttrs={{
                                    htmlAttributes: {
                                        maxlength: '100',
                                    },
                                }}
                                rules={{ required: 'Network Prefix is Required' }}
                            />
                            {errors.ipaNetworkPrefix && <p className='error-form'>{errors.ipaNetworkPrefix.message}</p>}
                        </div>
                    </Col>
                    <Col md='4'>
                        <div>
                            <label className='col-md-12 mb-1'>DOFR Agreement ID *</label>
                            <ControlledProviderDataTextBox
                                name='dofrAgreementId'
                                control={control}
                                boxAttrs={{
                                    htmlAttributes: {
                                        maxlength: '100',
                                    },
                                }}
                                rules={{ required: 'Agreement ID is Required' }}
                            />
                            {errors.dofrAgreementId && <p className='error-form'>{errors.dofrAgreementId.message}</p>}
                        </div>
                    </Col>
                </Row>
            </form>
        </DisabledWrapper>
    );
};
